/* eslint-disable import/no-mutable-exports */
/* eslint-disable no-console */
// Pusher Channels

import Pusher from 'pusher-js'

const debug = false
Pusher.logToConsole = debug

const NO_CONTEXT_KEY = '__none__'

const PusherClient = {
  pusher: null,
  wrappedCallbacks: new Map(),
  subscriptions: new Map(),
  dynamicChannels: new Set(),
  connectionState: 'idle',
  lastConnectionError: null,
  stateListeners: new Set(),
  connectionHandlers: null,
  callbackIds: new WeakMap(),
  contextObjectIds: new WeakMap(),
  primitiveContextIds: new Map(),
  nextCallbackId: 1,
  nextContextId: 1,
  clientInstanceId: 0,
  normalizePayload(payload) {
    if (typeof payload !== 'string') {
      return payload
    }

    try {
      return JSON.parse(payload)
    } catch (error) {
      return payload
    }
  },
  getCallbackIdentity(callback) {
    if (!this.callbackIds.has(callback)) {
      this.callbackIds.set(callback, this.nextCallbackId)
      this.nextCallbackId += 1
    }

    return `callback:${this.callbackIds.get(callback)}`
  },
  getContextIdentity(context) {
    if (context === null || context === undefined) {
      return `context:${NO_CONTEXT_KEY}`
    }

    if (typeof context === 'object' || typeof context === 'function') {
      if (!this.contextObjectIds.has(context)) {
        this.contextObjectIds.set(context, this.nextContextId)
        this.nextContextId += 1
      }

      return `context:${this.contextObjectIds.get(context)}`
    }

    const primitiveKey = `${typeof context}:${String(context)}`
    if (!this.primitiveContextIds.has(primitiveKey)) {
      this.primitiveContextIds.set(primitiveKey, this.nextContextId)
      this.nextContextId += 1
    }

    return `context:${this.primitiveContextIds.get(primitiveKey)}`
  },
  getSubscriptionKey(channelName, eventName, callback, context) {
    return [
      channelName,
      eventName,
      this.getCallbackIdentity(callback),
      this.getContextIdentity(context),
    ].join('::')
  },
  createSubscriptionEntry(channelType, channelName, eventName, callback, context) {
    const key = this.getSubscriptionKey(channelName, eventName, callback, context)

    return {
      key,
      channelType,
      channelName,
      eventName,
      callback,
      context,
      boundClientInstanceId: null,
    }
  },
  registerSubscription(channelType, channelName, eventName, callback, context) {
    const entry = this.createSubscriptionEntry(channelType, channelName, eventName, callback, context)
    this.subscriptions.set(entry.key, entry)

    if (channelType === 'dynamic') {
      this.dynamicChannels.add(channelName)
    }

    return entry
  },
  getWrappedCallback(entry) {
    const existingCallback = this.wrappedCallbacks.get(entry.key)
    if (existingCallback) {
      return existingCallback
    }

    const wrappedCallback = payload => entry.callback.call(entry.context || null, this.normalizePayload(payload))

    this.wrappedCallbacks.set(entry.key, wrappedCallback)

    return wrappedCallback
  },
  removeWrappedCallback(key) {
    this.wrappedCallbacks.delete(key)
  },
  logDebug(message, payload = null) {
    if (!debug) {
      return
    }

    if (payload === null) {
      console.info(`[Pusher] ${message}`)
      return
    }

    console.info(`[Pusher] ${message}`, payload)
  },
  emitStateChange(payload = null) {
    this.stateListeners.forEach(listener => {
      listener(this.getState(), payload)
    })
  },
  setConnectionState(state, payload = null) {
    this.connectionState = state
    this.logDebug(`Connection state changed to ${state}`, payload)
    this.emitStateChange(payload)
  },
  onStateChange(listener) {
    this.stateListeners.add(listener)

    return () => {
      this.stateListeners.delete(listener)
    }
  },
  getState() {
    return {
      connectionState: this.connectionState,
      lastConnectionError: this.lastConnectionError,
      subscriptions: Array.from(this.subscriptions.values()).map(subscription => ({
        channelName: subscription.channelName,
        eventName: subscription.eventName,
        channelType: subscription.channelType,
      })),
      dynamicChannels: Array.from(this.dynamicChannels.values()),
    }
  },
  findChannel(channelName, shouldSubscribe = false) {
    if (!this.pusher || !this.pusher.channels) {
      return null
    }

    const existingChannel = this.pusher.channels.find(channelName)
    if (existingChannel) {
      return existingChannel
    }

    if (!shouldSubscribe || !this.pusher.subscribe) {
      return null
    }

    return this.pusher.subscribe(channelName)
  },
  bindSubscription(entry, force = false) {
    if (!this.pusher) {
      return
    }

    const subscription = this.subscriptions.get(entry.key)
    if (!subscription) {
      return
    }

    if (!force && subscription.boundClientInstanceId === this.clientInstanceId) {
      return
    }

    const channel = this.findChannel(subscription.channelName, true)
    if (!channel) {
      return
    }

    channel.bind(
      subscription.eventName,
      this.getWrappedCallback(subscription),
    )

    this.subscriptions.set(subscription.key, {
      ...subscription,
      boundClientInstanceId: this.clientInstanceId,
    })
  },
  refreshDynamicChannels() {
    const dynamicChannels = new Set()

    this.subscriptions.forEach(subscription => {
      if (subscription.channelType === 'dynamic') {
        dynamicChannels.add(subscription.channelName)
      }
    })

    this.dynamicChannels = dynamicChannels
  },
  findMatchingSubscriptions(channelName, eventName, callback, context) {
    const callbackProvided = typeof callback === 'function'
    const contextProvided = context !== undefined
    const contextIdentity = contextProvided ? this.getContextIdentity(context) : null

    return Array.from(this.subscriptions.values()).filter(subscription => {
      if (subscription.channelName !== channelName || subscription.eventName !== eventName) {
        return false
      }

      if (callbackProvided && subscription.callback !== callback) {
        return false
      }

      if (contextProvided && this.getContextIdentity(subscription.context) !== contextIdentity) {
        return false
      }

      return true
    })
  },
  unbindSubscription(entry) {
    if (this.pusher && this.pusher.channels) {
      const channel = this.pusher.channels.find(entry.channelName)
      if (channel) {
        channel.unbind(
          entry.eventName,
          this.getWrappedCallback(entry),
        )
      }
    }

    this.subscriptions.delete(entry.key)
    this.removeWrappedCallback(entry.key)
    this.refreshDynamicChannels()
  },
  bind(channelName, eventName, callback, context) {
    const entry = this.registerSubscription('base', channelName, eventName, callback, context)
    this.bindSubscription(entry)
  },
  bindChannel(channelName, eventName, callback, context) {
    const entry = this.registerSubscription('dynamic', channelName, eventName, callback, context)
    this.bindSubscription(entry)
  },
  unbind(channelName, eventName, callback, context) {
    if (typeof callback !== 'function') {
      this.logDebug('Skipping unbind without callback reference.', {
        channelName,
        eventName,
      })

      return
    }

    this.findMatchingSubscriptions(channelName, eventName, callback, context)
      .forEach(subscription => {
        this.unbindSubscription(subscription)
      })
  },
  unbindChannel(channelName, eventName, callback, context) {
    this.unbind(channelName, eventName, callback, context)
  },
  resetBindingsForNewClient() {
    this.wrappedCallbacks = new Map()
    this.subscriptions.forEach(subscription => {
      this.subscriptions.set(subscription.key, {
        ...subscription,
        boundClientInstanceId: null,
      })
    })
  },
  resubscribeAll() {
    this.subscriptions.forEach(subscription => {
      this.bindSubscription(subscription, true)
    })
  },
  ensureConnectionHandlers() {
    if (this.connectionHandlers) {
      return this.connectionHandlers
    }

    this.connectionHandlers = {
      error: payload => {
        this.lastConnectionError = payload
        this.logDebug('Connection error received', payload)
        this.emitStateChange(payload)
      },
      state_change: payload => {
        const nextState = payload?.current || payload?.state
        if (!nextState) {
          return
        }

        if (nextState === 'connected') {
          this.lastConnectionError = null
        }

        this.setConnectionState(nextState, payload)
      },
    }

    return this.connectionHandlers
  },
  bindConnectionEvents() {
    if (!this.pusher?.connection?.bind) {
      return
    }

    const handlers = this.ensureConnectionHandlers()
    Object.entries(handlers).forEach(([eventName, handler]) => {
      this.pusher.connection.bind(eventName, handler)
    })
  },
  init(key, cluster = 'eu') {
    if (this.pusher && typeof this.pusher.disconnect === 'function') {
      this.pusher.disconnect()
    }

    this.clientInstanceId += 1
    this.pusher = new Pusher(
      key,
      {
        cluster,
      },
    )
    this.resetBindingsForNewClient()
    this.lastConnectionError = null
    this.setConnectionState('initializing', { clientInstanceId: this.clientInstanceId })
    this.bindConnectionEvents()
    this.notifications = this.findChannel('notifications', true)
    this.update = this.findChannel('update', true)
    this.resubscribeAll()
  },
}

export default PusherClient
